import type {
  TimelineSearchParams,
  TimelineSearchFilter,
  ChannelSessionQueryParams,
  ConversationDocumentSearchParams,
} from '../../types/cache/query-matcher.types';
import type { QueryKey } from '@tanstack/react-query';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getQueryParams = (
  matchedKey: QueryKey
): Record<string, unknown> | null => {
  if (!Array.isArray(matchedKey) || matchedKey.length < 2) {
    return null;
  }

  return isRecord(matchedKey[1]) ? matchedKey[1] : null;
};

const isFilterValue = (
  value: unknown
): value is string | number | (string | number)[] =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  (Array.isArray(value) &&
    value.every(
      (item) => typeof item === 'string' || typeof item === 'number'
    ));

const isTimelineSearchFilter = (
  value: unknown
): value is TimelineSearchFilter =>
  isRecord(value) &&
  (value.field === undefined || typeof value.field === 'string') &&
  (value.value === undefined || isFilterValue(value.value));

/**
 * Extracts normalized timeline search params from a React Query key.
 *
 * @param matchedKey - Query key matched by React Query cache filters.
 * @returns Timeline search params or null when the key has no params object.
 */
export const getTimelineSearchParams = (
  matchedKey: QueryKey
): TimelineSearchParams | null => {
  const params = getQueryParams(matchedKey);
  if (!params) {
    return null;
  }

  return {
    search: typeof params.search === 'string' ? params.search : undefined,
    filters: Array.isArray(params.filters)
      ? params.filters.filter(isTimelineSearchFilter)
      : undefined,
  };
};

/**
 * Extracts normalized channel-session list params from a React Query key.
 *
 * @param matchedKey - Query key matched by React Query cache filters.
 * @returns Channel-session params or null when the key has no params object.
 */
export const getChannelSessionQueryParams = (
  matchedKey: QueryKey
): ChannelSessionQueryParams | null => {
  const params = getQueryParams(matchedKey);
  if (!params) {
    return null;
  }

  return {
    filterBy: typeof params.filterBy === 'string' ? params.filterBy : undefined,
  };
};

/**
 * Extracts normalized conversation-document search params from a React Query key.
 *
 * @param matchedKey - Query key matched by React Query cache filters.
 * @returns Conversation-document params or null when the key has no params object.
 */
export const getConversationDocumentSearchParams = (
  matchedKey: QueryKey
): ConversationDocumentSearchParams | null => {
  const params = getQueryParams(matchedKey);
  if (!params) {
    return null;
  }

  return {
    sort: Array.isArray(params.sort)
      ? params.sort.filter(isRecord).map((sort) => ({
          field: typeof sort.field === 'string' ? sort.field : undefined,
          direction:
            typeof sort.direction === 'string' ? sort.direction : undefined,
        }))
      : undefined,
  };
};

/**
 * Checks whether a string candidate matches a scalar or array query value.
 *
 * @param candidate - Item value represented as a string.
 * @param value - Query filter value to compare.
 * @returns Whether the candidate is represented by the query value.
 */
export const valueMatches = (
  candidate: string | undefined,
  value?: string | number | (string | number)[]
): boolean => {
  if (!candidate || value === undefined) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.some((item) => item.toString() === candidate);
  }

  return value.toString() === candidate;
};

/**
 * Checks whether a timeline item belongs in a matched timeline query.
 *
 * @param matchedKey - Timeline query key.
 * @param item - Timeline-like document with searchable ids.
 * @returns Whether the item satisfies supported timeline filters.
 */
export const shouldAddTimelineItemToQuery = <
  TItem extends {
    contactId?: string;
    funnelConversationId?: string;
    funnelId?: string;
  },
>(
  matchedKey: QueryKey,
  item: TItem
): boolean => {
  const params = getTimelineSearchParams(matchedKey);
  if (!params || params.search?.trim()) return false;
  const filters = params.filters ?? [];

  if (filters.length === 0) {
    return true;
  }

  return filters.every((filter) => {
    if (
      filter.or ||
      (filter.operator && !['eq', 'in'].includes(filter.operator))
    ) {
      return false;
    }
    if (filter.field === 'contactId') {
      return valueMatches(item.contactId, filter.value);
    }

    if (filter.field === 'funnelConversationId') {
      return valueMatches(item.funnelConversationId, filter.value);
    }

    if (filter.field === 'funnelId') {
      return valueMatches(item.funnelId, filter.value);
    }

    return false;
  });
};

/**
 * Parses backend `filterBy` expressions into a field-value map.
 *
 * @param filterBy - Semicolon-delimited filter expression.
 * @returns Parsed filter values keyed by field.
 */
export const parseFilterByExpression = (
  filterBy?: string
): Record<string, string> => {
  if (!filterBy) {
    return {};
  }

  return filterBy.split(';').reduce<Record<string, string>>((acc, filter) => {
    const [field, value] = filter.split('=');
    if (field && value) {
      acc[field] = value.replace(/^"|"$/g, '');
    }
    return acc;
  }, {});
};

/**
 * Checks whether a channel session belongs in a matched channel-session query.
 *
 * @param matchedKey - Channel-session query key.
 * @param channelSession - Channel-session-like object.
 * @returns Whether the item satisfies supported channel-session filters.
 */
export const shouldAddChannelSessionToQuery = <
  TChannelSession extends {
    funnelConversationId: number;
    archivedAt?: string | null;
    messagingConversationId?: string | null;
    id?: number;
    channelType?: string;
  },
>(
  matchedKey: QueryKey,
  channelSession: TChannelSession
): boolean => {
  const params = getChannelSessionQueryParams(matchedKey);
  const filters = parseFilterByExpression(params?.filterBy);

  if (!params) return false;
  const values: Record<string, string | undefined> = {
    funnelConversationId: String(channelSession.funnelConversationId),
    archivedAt: channelSession.archivedAt ?? 'null',
    messagingConversationId: channelSession.messagingConversationId ?? 'null',
    id: channelSession.id?.toString(),
    channelType: channelSession.channelType,
  };
  return Object.entries(filters).every(
    ([field, value]) => values[field] === value
  );
};

/**
 * Checks whether a conversation-document query is sorted by latest interaction.
 *
 * @param matchedKey - Conversation-document query key.
 * @returns Whether latest interactions should be promoted to the first page.
 */
export const isSortedByLatestInteraction = (matchedKey: QueryKey): boolean => {
  const params = getConversationDocumentSearchParams(matchedKey);

  return (
    params?.sort?.some(
      (sort) =>
        sort.field === 'lastInteractionAt' &&
        sort.direction?.toLowerCase() === 'desc'
    ) ?? false
  );
};
